<script setup lang="ts">

/*
	OptionsSidebar.vue

	Right-side panel for editing properties of selected objects (labels or
	seat rows). Dynamically shows relevant controls based on selection type
	with two-way binding to parent state.

	Features:
	- Label mode: Text content, font size/family, color picker, position info
	- Row mode: Duplicate/reset rows, custom row label, label position, seat count, starting number, reverse numbering, curvature, rotation, and skew controls
	- Seat mode: Custom seat label (e.g. INVA, VIP) and zone assignment
	- Responsive: Fixed right panel on desktop, bottom sheet on mobile
	- All controls use two-way computed properties with emit-based parent updates

	Note: the prototype this was ported from also supported table/furniture modes
	(for the table map editor). Those aren't wired up yet, so this component only
	implements the seat map's 'label', 'row', 'seat' and 'shape' modes for now.
*/

import type { DecorativeShape, Label, RowLabelPosition, Seat, SeatmapZone } from '~/lib/seatmapeditor/types'

type SidebarMode = 'label' | 'row' | 'shape' | 'seat'

type RowLike = {
	seats: unknown[]
	rotation?: number
	curvature?: number
	skew?: number
	label?: string
	startNumber?: number
	reverseNumbering?: boolean
	labelPosition?: RowLabelPosition
}

type SelectedRowItem = {
	rowIndex: number
	row: RowLike
}

type SelectedSeatItem = {
	rowIndex: number
	seatIndex: number
	seat: Seat
}

interface Props {
	mode: SidebarMode | null
	isOpen: boolean
	label: Label | null
	shape: DecorativeShape | null
	selectedRows?: SelectedRowItem[]
	selectedSeats?: SelectedSeatItem[]
	zones?: SeatmapZone[]
}

interface Emits {
	(e: 'close'): void
	(e: 'update-text', text: string): void
	(e: 'update-font-size', size: number): void
	(e: 'update-font-family', family: string): void
	(e: 'update-color', color: string): void
	(e: 'duplicate-rows'): void
	(e: 'reset-rows'): void
	(e: 'update-seats', count: number): void
	(e: 'update-start-number', startNumber: number): void
	(e: 'update-reverse-numbering', reverseNumbering: boolean): void
	(e: 'update-row-label', label: string): void
	(e: 'update-row-label-position', position: RowLabelPosition): void
	(e: 'update-seat-title', title: string): void
	(e: 'update-seat-zone', zoneId: number | null): void
	(e: 'manage-zones'): void
	(e: 'update-rotation', degrees: number): void
	(e: 'update-curvature', curvature: number): void
	(e: 'update-skew', skew: number): void
	(e: 'update-shape-width', width: number): void
	(e: 'update-shape-height', height: number): void
	(e: 'update-shape-corner-radius', radius: number): void
	(e: 'update-shape-fill', fill: string): void
	(e: 'update-shape-border', border: string | null): void
	(e: 'update-shape-text', text: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { $translate } = useNuxtApp()
const _ = $translate as (key: string) => string

const fontOptions = ['Segoe UI', 'Arial', 'Georgia', 'Times New Roman', 'Courier New', 'Verdana']

const localText = computed({
	get: () => props.label?.text ?? '',
	set: (value) => {
		if (props.label) {
			emit('update-text', value)
		}
	}
})

const localFontSize = computed({
	get: () => props.label?.fontSize ?? 16,
	set: (value) => {
		if (props.label) {
			emit('update-font-size', value)
		}
	}
})

const localFontFamily = computed({
	get: () => props.label?.fontFamily ?? 'Segoe UI',
	set: (value) => {
		if (props.label) {
			emit('update-font-family', value)
		}
	}
})

const localColor = computed({
	get: () => props.label?.fill ?? '#0284c7',
	set: (value) => {
		if (props.label) {
			emit('update-color', value)
		}
	}
})

const localShapeWidth = computed({
	get: () => props.shape?.w ?? 0,
	set: (value) => {
		if (props.shape) {
			emit('update-shape-width', value)
		}
	}
})

const localShapeHeight = computed({
	get: () => props.shape?.h ?? 0,
	set: (value) => {
		if (props.shape) {
			emit('update-shape-height', value)
		}
	}
})

const localShapeCornerRadius = computed({
	get: () => props.shape?.r ?? 0,
	set: (value) => {
		if (props.shape) {
			emit('update-shape-corner-radius', value)
		}
	}
})

const localShapeFill = computed({
	get: () => props.shape?.fill ?? '#334155',
	set: (value) => {
		if (props.shape) {
			emit('update-shape-fill', value)
		}
	}
})

const hasShapeBorder = computed({
	get: () => !!props.shape?.border,
	set: (value) => {
		if (props.shape) {
			emit('update-shape-border', value ? (props.shape.border || '#0f172a') : null)
		}
	}
})

const localShapeBorder = computed({
	get: () => props.shape?.border ?? '#0f172a',
	set: (value) => {
		if (props.shape) {
			emit('update-shape-border', value)
		}
	}
})

const localShapeText = computed({
	get: () => props.shape?.text ?? '',
	set: (value) => {
		if (props.shape) {
			emit('update-shape-text', value)
		}
	}
})

const normalizedSelectedRows = computed<SelectedRowItem[]>(() => props.selectedRows ?? [])

const primaryRow = computed(() => normalizedSelectedRows.value[0] ?? null)

const localRowSeatCount = computed({
	get: () => primaryRow.value?.row.seats.length ?? 1,
	set: (value) => {
		if (primaryRow.value) {
			emit('update-seats', value)
		}
	}
})

const localRowStartNumber = computed({
	get: () => primaryRow.value?.row.startNumber ?? 1,
	set: (value) => {
		if (primaryRow.value) {
			emit('update-start-number', value)
		}
	}
})

const localRowReverseNumbering = computed({
	get: () => !!primaryRow.value?.row.reverseNumbering,
	set: (value) => {
		if (primaryRow.value) {
			emit('update-reverse-numbering', value)
		}
	}
})

const localRowLabel = computed({
	get: () => primaryRow.value?.row.label ?? '',
	set: (value) => {
		if (primaryRow.value) {
			emit('update-row-label', value)
		}
	}
})

const localRowLabelPosition = computed({
	get: (): RowLabelPosition => primaryRow.value?.row.labelPosition === 'end' ? 'end' : 'start',
	set: (value: RowLabelPosition) => {
		if (primaryRow.value) {
			emit('update-row-label-position', value)
		}
	}
})

const localRowCurvature = computed({
	get: () => primaryRow.value?.row.curvature ?? 0,
	set: (value) => {
		if (primaryRow.value) {
			emit('update-curvature', value)
		}
	}
})

const localRowRotation = computed({
	get: () => primaryRow.value?.row.rotation ?? 0,
	set: (value) => {
		if (primaryRow.value) {
			emit('update-rotation', value)
		}
	}
})

const localRowSkew = computed({
	get: () => primaryRow.value?.row.skew ?? 0,
	set: (value) => {
		if (primaryRow.value) {
			emit('update-skew', value)
		}
	}
})

const selectedRowCountLabel = computed(() => {
	return normalizedSelectedRows.value.length === 1
		? _('1 row selected')
		: _('{count} rows selected').replace('{count}', String(normalizedSelectedRows.value.length))
})

const normalizedSelectedSeats = computed<SelectedSeatItem[]>(() => props.selectedSeats ?? [])

const primarySeat = computed(() => normalizedSelectedSeats.value[0] ?? null)

const localSeatTitle = computed({
	get: () => {
		const seat = primarySeat.value?.seat
		if (!seat) {
			return ''
		}
		const custom = seat.title != null ? String(seat.title) : ''
		if (custom !== '') {
			return custom
		}
		return String(seat.index ?? primarySeat.value?.seatIndex + 1 ?? '')
	},
	set: (value) => {
		if (primarySeat.value) {
			emit('update-seat-title', value)
		}
	}
})

const seatmapZones = computed<SeatmapZone[]>(() => props.zones ?? [])

const localSeatZone = computed({
	get: () => {
		const zone = primarySeat.value?.seat?.zone
		if (zone == null || zone === '') {
			return ''
		}
		return String(zone)
	},
	set: (value: string) => {
		if (!primarySeat.value) {
			return
		}
		if (value === '' || value == null) {
			emit('update-seat-zone', null)
			return
		}
		const parsed = Number(value)
		emit('update-seat-zone', Number.isFinite(parsed) ? parsed : null)
	}
})

const selectedSeatCountLabel = computed(() => {
	return normalizedSelectedSeats.value.length === 1
		? _('1 seat selected')
		: _('{count} seats selected').replace('{count}', String(normalizedSelectedSeats.value.length))
})

const adjustRowSeatCount = (delta: number) => {
	localRowSeatCount.value = Math.max(1, localRowSeatCount.value + delta)
}

const adjustRowStartNumber = (delta: number) => {
	localRowStartNumber.value = Math.max(1, localRowStartNumber.value + delta)
}

const panelTitle = computed(() => {
	if (props.mode === 'label') {
		return props.label ? _('Label {id}').replace('{id}', String(props.label.id)) : _('Label options')
	}

	if (props.mode === 'row') {
		return _('Row settings')
	}

	if (props.mode === 'seat') {
		return _('Seat settings')
	}

	if (props.mode === 'shape') {
		return _('Screen options')
	}

	return _('Options')
})

const isLabelMode = computed(() => props.mode === 'label' && !!props.label)
const isRowMode = computed(() => props.mode === 'row' && normalizedSelectedRows.value.length > 0)
const isSeatMode = computed(() => props.mode === 'seat' && normalizedSelectedSeats.value.length > 0)
const isShapeMode = computed(() => props.mode === 'shape' && !!props.shape)
const hasVisibleContent = computed(() => isLabelMode.value || isRowMode.value || isSeatMode.value || isShapeMode.value)
</script>

<template>
	<transition name="options-panel-transition">
		<aside v-if="isOpen && hasVisibleContent" class="options-panel panel panel-default">
			<div class="panel-heading">
				<button type="button" class="close" :aria-label="_('Close')" @click="emit('close')"><span aria-hidden="true">&times;</span></button>
				<h4 class="panel-title">{{ panelTitle }}</h4>
				<p v-if="isRowMode" class="options-hint options-panel-subtitle">{{ selectedRowCountLabel }}</p>
				<p v-else-if="isSeatMode" class="options-hint options-panel-subtitle">{{ selectedSeatCountLabel }}</p>
			</div>

			<div class="panel-body options-panel-body">
				<template v-if="isLabelMode">
					<div class="form-group">
						<label>{{ _('Text content') }}</label>
						<input v-model="localText" type="text" class="form-control" :placeholder="_('Enter label text')" />
					</div>

					<div class="form-group">
						<label>{{ _('Font size') }}</label>
						<div class="input-group">
							<span class="input-group-btn">
								<button type="button" class="btn btn-default" @click="localFontSize = Math.max(8, localFontSize - 2)"><i class="fa fa-fw fa-minus"></i></button>
							</span>
							<input v-model.number="localFontSize" type="number" class="form-control text-center" min="8" max="72" />
							<span class="input-group-btn">
								<button type="button" class="btn btn-default" @click="localFontSize = Math.min(72, localFontSize + 2)"><i class="fa fa-fw fa-plus"></i></button>
							</span>
						</div>
					</div>

					<div class="form-group">
						<label>{{ _('Font family') }}</label>
						<select v-model="localFontFamily" class="form-control">
							<option v-for="font in fontOptions" :key="font" :value="font">{{ font }}</option>
						</select>
					</div>

					<div class="form-group">
						<label>{{ _('Color') }}</label>
						<div class="options-color-row">
							<input v-model="localColor" type="color" class="options-color-input" />
							<span class="options-color-value">{{ localColor }}</span>
						</div>
					</div>

					<p class="options-hint"><strong>{{ _('Position') }}:</strong> ({{ Math.round(label?.x ?? 0) }}, {{ Math.round(label?.y ?? 0) }})</p>
				</template>

				<template v-else-if="isShapeMode">
					<div class="form-group">
						<label>{{ _('Text (optional)') }}</label>
						<input v-model="localShapeText" type="text" class="form-control" :placeholder="_('Screen').toUpperCase()" />
						<p class="help-block">{{ _('Leave empty to show the default "SCREEN" label.') }}</p>
					</div>

					<div class="form-group">
						<label>{{ _('Width') }}</label>
						<input v-model.number="localShapeWidth" type="number" class="form-control" min="24" />
					</div>

					<div class="form-group">
						<label>{{ _('Height') }}</label>
						<input v-model.number="localShapeHeight" type="number" class="form-control" min="24" />
					</div>

					<div class="form-group">
						<label>{{ _('Corner radius') }}</label>
						<input v-model.number="localShapeCornerRadius" type="range" class="options-slider" min="0" max="60" step="1" />
						<div class="input-group">
							<input v-model.number="localShapeCornerRadius" type="number" class="form-control text-center" min="0" max="60" step="1" />
							<span class="input-group-addon">{{ localShapeCornerRadius }}</span>
						</div>
					</div>

					<div class="form-group">
						<label>{{ _('Fill color') }}</label>
						<div class="options-color-row">
							<input v-model="localShapeFill" type="color" class="options-color-input" />
							<span class="options-color-value">{{ localShapeFill }}</span>
						</div>
					</div>

					<div class="form-group">
						<div class="checkbox">
							<label>
								<input v-model="hasShapeBorder" type="checkbox" />
								{{ _('Border') }}
							</label>
						</div>
						<div v-if="hasShapeBorder" class="options-color-row">
							<input v-model="localShapeBorder" type="color" class="options-color-input" />
							<span class="options-color-value">{{ localShapeBorder }}</span>
						</div>
					</div>

					<p class="options-hint"><strong>{{ _('Position') }}:</strong> ({{ Math.round(shape?.x ?? 0) }}, {{ Math.round(shape?.y ?? 0) }})</p>
				</template>

				<template v-else-if="isRowMode">
					<div class="form-group options-btn-row">
						<button type="button" class="btn btn-default btn-block" @click="emit('duplicate-rows')">
							<i class="fa fa-fw fa-copy"></i> {{ _('Duplicate selected rows') }}
						</button>
						<button type="button" class="btn btn-default btn-block" @click="emit('reset-rows')">
							<i class="fa fa-fw fa-rotate-left"></i> {{ _('Reset to default') }}
						</button>
					</div>

					<div class="form-group">
						<label>{{ _('Row') }}</label>
						<input v-model="localRowLabel" type="text" class="form-control" placeholder="R1" />
					</div>

					<div class="form-group">
						<label>{{ _('Row label position') }}</label>
						<select v-model="localRowLabelPosition" class="form-control">
							<option value="start">{{ _('Start of row') }}</option>
							<option value="end">{{ _('End of row') }}</option>
						</select>
					</div>

					<div class="form-group">
						<label>{{ _('Seats') }}</label>
						<div class="input-group">
							<span class="input-group-btn">
								<button type="button" class="btn btn-default" @click="adjustRowSeatCount(-1)"><i class="fa fa-fw fa-minus"></i></button>
							</span>
							<input v-model.number="localRowSeatCount" type="number" class="form-control text-center" min="1" />
							<span class="input-group-btn">
								<button type="button" class="btn btn-default" @click="adjustRowSeatCount(1)"><i class="fa fa-fw fa-plus"></i></button>
							</span>
						</div>
					</div>

					<div class="form-group">
						<label>{{ _('Starting number') }}</label>
						<div class="input-group">
							<span class="input-group-btn">
								<button type="button" class="btn btn-default" @click="adjustRowStartNumber(-1)"><i class="fa fa-fw fa-minus"></i></button>
							</span>
							<input v-model.number="localRowStartNumber" type="number" class="form-control text-center" min="1" />
							<span class="input-group-btn">
								<button type="button" class="btn btn-default" @click="adjustRowStartNumber(1)"><i class="fa fa-fw fa-plus"></i></button>
							</span>
						</div>
					</div>

					<div class="form-group">
						<div class="checkbox">
							<label>
								<input v-model="localRowReverseNumbering" type="checkbox" />
								{{ _('Reverse numbering direction') }}
							</label>
						</div>
					</div>

					<div class="form-group">
						<label>{{ _('Row curvature') }}</label>
						<input v-model.number="localRowCurvature" type="range" class="options-slider" min="-120" max="120" step="1" />
						<div class="input-group">
							<input v-model.number="localRowCurvature" type="number" class="form-control text-center" min="-120" max="120" step="1" />
							<span class="input-group-addon">{{ localRowCurvature }}</span>
						</div>
					</div>

					<div class="form-group">
						<label>{{ _('Row rotation') }}</label>
						<input v-model.number="localRowRotation" type="range" class="options-slider" min="-180" max="180" step="1" />
						<div class="input-group">
							<input v-model.number="localRowRotation" type="number" class="form-control text-center" min="-180" max="180" step="1" />
							<span class="input-group-addon">{{ localRowRotation }}&deg;</span>
						</div>
					</div>

					<div class="form-group">
						<label>{{ _('Row skew') }}</label>
						<input v-model.number="localRowSkew" type="range" class="options-slider" min="-120" max="120" step="1" />
						<div class="input-group">
							<input v-model.number="localRowSkew" type="number" class="form-control text-center" min="-120" max="120" step="1" />
							<span class="input-group-addon">{{ localRowSkew }}</span>
						</div>
						<p class="help-block"><strong>{{ _('Tip') }}:</strong> {{ _("Settings don't update in real time if you only have 1 row.") }}</p>
					</div>
				</template>

				<template v-else-if="isSeatMode">
					<div class="form-group">
						<label>{{ _('Seat') }}</label>
						<input v-model="localSeatTitle" type="text" class="form-control" placeholder="VIP" />
					</div>

					<div class="form-group">
						<label>{{ _('Zone') }}</label>
						<select v-model="localSeatZone" class="form-control">
							<option value="">{{ _('Default') }}</option>
							<option v-for="zone in seatmapZones" :key="zone.id" :value="String(zone.id)">{{ zone.name }}</option>
						</select>
					</div>

					<div class="form-group">
						<button type="button" class="btn btn-default btn-block" @click="emit('manage-zones')">
							<i class="fa fa-fw fa-cog"></i> {{ _('Manage zones') }}
						</button>
					</div>
				</template>
			</div>
		</aside>
	</transition>
</template>

<style scoped>
.options-panel {
	position: fixed;
	right: 12px;
	top: 84px;
	bottom: 12px;
	width: 300px;
	overflow-y: auto;
	z-index: 2005;
	display: flex;
	flex-direction: column;
	margin-bottom: 0;
}

.panel-heading {
	position: relative;
	flex-shrink: 0;
}

.panel-heading .panel-title {
	margin: 0;
}

.options-hint {
	color: #6c757d;
}

.options-panel-subtitle {
	margin: 4px 0 0;
}

.options-panel-body {
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.options-btn-row {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.options-slider {
	width: 100%;
	margin-bottom: 8px;
}

.options-color-row {
	display: flex;
	align-items: center;
	gap: 10px;
}

.options-color-input {
	width: 50px;
	height: 34px;
	padding: 2px;
	cursor: pointer;
}

.options-color-value {
	font-family: 'Courier New', monospace;
}

.options-panel-transition-enter-active,
.options-panel-transition-leave-active {
	transition: transform 0.2s ease;
}

.options-panel-transition-enter-from,
.options-panel-transition-leave-to {
	transform: translateX(100%);
}

@media (max-width: 900px) {
	.options-panel {
		width: auto;
		left: 12px;
		right: 12px;
		top: auto;
		bottom: 12px;
		max-height: min(68vh, 520px);
	}

	.options-panel-transition-enter-from,
	.options-panel-transition-leave-to {
		transform: translateY(100%);
	}
}
</style>
